import { Location } from 'history';
import React from 'react';
import { Params, useLocation, useParams } from 'react-router';

type WithLocationProps = {
    children: (location: Location) => React.ReactNode;
};

export function WithLocation(props: WithLocationProps) {
    const location = useLocation();
    return props.children(location);
}

type WithSearchProps = {
    children: (search: Map<string, string>) => React.ReactNode;
};

export function WithSearch(props: WithSearchProps) {
    const location = useLocation();
    return <>{props.children(queryStringAsMap(location.search))}</>;
}

type WithPathnameProps = {
    children: (pathname: string) => React.ReactNode;
};

export function WithPathName(props: WithPathnameProps) {
    const location = useLocation();
    return <>{props.children(location.pathname)}</>;
}

type WithParamProps<ParamsOrKey> = {
    children: (
        params: Readonly<[ParamsOrKey] extends [string] ? Params<ParamsOrKey> : Partial<ParamsOrKey>>
    ) => React.ReactNode;
};

export function WithParams<T extends string = string>(props: WithParamProps<T>) {
    const params = useParams<T>();
    return <>{props.children(params)}</>;
}

type SearchProps = {
    search: Map<string, string>;
};

export function withSearch<P extends SearchProps>(WrappedComponent: React.ComponentType<P>) {
    return function componentWithSearch(props: Omit<P, keyof SearchProps>) {
        return <WithSearch>{(search) => <WrappedComponent {...(props as P)} search={search} />}</WithSearch>;
    };
}

type PathnameProps = {
    pathname: string;
};

export function withPathname<P>(WrappedComponent: React.ComponentType<P>) {
    return function componentWithPathname(props: Omit<P, keyof PathnameProps>) {
        return <WithPathName>{(search) => <WrappedComponent {...(props as P)} pathname={search} />}</WithPathName>;
    };
}

export function queryStringAsMap(queryString: string): Map<string, string> {
    // No params for ie11 users
    if (typeof URLSearchParams === 'undefined') return new Map();

    const params = new URLSearchParams(queryString);
    const map = new Map<string, string>();
    params.forEach((v, k) => {
        map.set(k, v);
    });
    return map;
}
